import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const isActive = (path: string) => {
    return location.pathname === path ? 'bg-lemon' : '';
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="flex min-h-screen">
      {/* Mobile Menu Button - Moved to top-right */}
      <div className="lg:hidden fixed top-4 right-4 z-50">
        <button 
          onClick={toggleMobileMenu}
          className="p-2 bg-yellow-100 rounded-lg shadow-md"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Navigation Overlay */}
      {isMobileMenuOpen && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={toggleMobileMenu}
        />
      )}

      {/* Sidebar Navigation - Desktop and Mobile */}
      <nav 
        className={`
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0 
          w-64 bg-white shadow-lg fixed h-full p-4 z-40
          transition-transform duration-300 ease-in-out
        `}
      >
        <div className="mb-10 pt-6 text-center">
          <h1 className="text-3xl font-baloo font-bold text-yellow-500">Besties</h1>
          <h1 className="text-3xl font-baloo font-bold text-yellow-500">Lemonade</h1>
        </div>
        
        <ul className="space-y-4 font-fredoka text-xl">
          <li>
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
              <motion.div 
                className={`nav-item ${isActive('/')}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                🏠 Home
              </motion.div>
            </Link>
          </li>
          <li>
            <Link to="/menu" onClick={() => setIsMobileMenuOpen(false)}>
              <motion.div 
                className={`nav-item ${isActive('/menu')}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                🍋 Menu
              </motion.div>
            </Link>
          </li>
          <li>
            <Link to="/about" onClick={() => setIsMobileMenuOpen(false)}>
              <motion.div 
                className={`nav-item ${isActive('/about')}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                👧 About Us
              </motion.div>
            </Link>
          </li>
          <li>
            <Link to="/schedule" onClick={() => setIsMobileMenuOpen(false)}>
              <motion.div 
                className={`nav-item ${isActive('/schedule')}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                📅 Schedule
              </motion.div>
            </Link>
          </li>
          <li>
            <Link to="/refunds" onClick={() => setIsMobileMenuOpen(false)}>
              <motion.div 
                className={`nav-item ${isActive('/refunds')}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                💰 Refund Policy
              </motion.div>
            </Link>
          </li>
        </ul>
      </nav>

      {/* Main Content - Added padding-top for mobile */}
      <div className="flex-1 lg:ml-64 p-6 pt-12 lg:pt-6">
        {children}
      </div>
    </div>
  );
};

export default MainLayout; 